import React, { useState, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import "../styles/OneOnOneReport.css";

const OneOnOneReport = ({ userData, role, handleModuleChange }) => {
  // Retrieve logged in user/manager stored in localStorage
  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userData") || localStorage.getItem("user") || "{}");
    } catch (e) {
      return {};
    }
  }, []);

  // Helper to format Date as YYYY-MM-DD
  const formatDateString = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Initial date defaults (Blank on page load/refresh)
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [rawMeetings, setRawMeetings] = useState([]);
  const [employeesMap, setEmployeesMap] = useState({});
  const [loading, setLoading] = useState(true);

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleFromDateChange = (val) => {
    setFromDate(val);
  };

  const handleToDateChange = (val) => {
    setToDate(val);
  };

  // Fetch Employee directory to map employee names -> emails
  useEffect(() => {
    fetch("/gt-api/get_employees.php", {
      credentials: "include"
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.employees)) {
          const map = {};
          data.employees.forEach((emp) => {
            const fullName = `${emp.FirstName || ""} ${emp.LastName || ""}`.trim().toLowerCase();
            const email = emp.EmailId || emp.email || emp.Email || "";
            const dept = emp.DepartmentName || emp.Department || emp.dept || "";

            if (emp.id) map[emp.id] = { email, dept, name: `${emp.FirstName} ${emp.LastName}` };
            if (emp.EmpId) map[emp.EmpId] = { email, dept, name: `${emp.FirstName} ${emp.LastName}` };
            if (fullName) map[fullName] = { email, dept, name: `${emp.FirstName} ${emp.LastName}` };
          });
          setEmployeesMap(map);
        }
      })
      .catch((err) => console.error("Error loading employees for email map:", err));
  }, []);

  // Fetch 1-on-1 meetings data
  const fetchReportData = (fDate = fromDate, tDate = toDate) => {
    if (!fDate || !tDate) {
      setRawMeetings([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const mgrId = userData?.id || storedUser?.id || storedUser?.EmpId || "";
    const userRole = role || storedUser?.role || "";
    const primaryUrl = `/gt-api/get_missed_oneonone_report.php?from_date=${fDate}&to_date=${tDate}&manager_id=${mgrId}&role=${userRole}`;
    const fallbackUrl = `/gt-api/get_oneonone.php?manager_id=${mgrId}&role=${userRole || "manager"}`;

    fetch(primaryUrl, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.meetings)) {
          setRawMeetings(data.meetings);
          setLoading(false);
        } else {
          // Fallback to get_oneonone.php
          fetch(fallbackUrl, { credentials: "include" })
            .then((res) => res.json())
            .then((fbData) => {
              setRawMeetings(fbData.meetings || []);
              setLoading(false);
            })
            .catch(() => setLoading(false));
        }
      })
      .catch(() => {
        // Fallback to get_oneonone.php
        fetch(fallbackUrl, { credentials: "include" })
          .then((res) => res.json())
          .then((fbData) => {
            setRawMeetings(fbData.meetings || []);
            setLoading(false);
          })
          .catch(() => setLoading(false));
      });
  };

  useEffect(() => {
    if (fromDate && toDate) {
      fetchReportData(fromDate, toDate);
    } else {
      setRawMeetings([]);
      setLoading(false);
    }
  }, [fromDate, toDate, userData?.id, role]);

  // Quick Preset Handlers
  const handlePreset = (preset) => {
    const today = new Date();
    let start, end;
    if (preset === "thisMonth") {
      start = formatDateString(new Date(today.getFullYear(), today.getMonth(), 1));
      end = formatDateString(today);
    } else if (preset === "lastMonth") {
      start = formatDateString(new Date(today.getFullYear(), today.getMonth() - 1, 1));
      end = formatDateString(new Date(today.getFullYear(), today.getMonth(), 0));
    } else if (preset === "last30") {
      const d = new Date();
      d.setDate(today.getDate() - 30);
      start = formatDateString(d);
      end = formatDateString(today);
    }

    if (start && end) {
      handleFromDateChange(start);
      handleToDateChange(end);
    }
  };

  // Date Normalizer Helper
  const parseToDateOnly = (str) => {
    if (!str) return "";
    const datePart = str.split(" ")[0].trim();
    if (datePart.includes("-")) {
      const parts = datePart.split("-");
      if (parts[0].length === 4) return datePart; // YYYY-MM-DD
      if (parts[2].length === 4) return `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`; // DD-MM-YYYY
    }
    return datePart;
  };

  // Filter missed meetings by search term and date range
  const filteredMissedMeetings = useMemo(() => {
    if (!fromDate || !toDate) {
      return [];
    }

    return rawMeetings.filter((m) => {
      const statusMatch = !m.status || m.status.toUpperCase() === "MISSED" || m.status === "0";
      const rawDateStr = m.meet_date || m.date || "";
      const meetDateOnly = parseToDateOnly(rawDateStr);

      if (!statusMatch) return false;

      if (fromDate && meetDateOnly && meetDateOnly < fromDate) {
        return false;
      }
      if (toDate && meetDateOnly && meetDateOnly > toDate) {
        return false;
      }

      // Filter strictly by logged-in Manager so a logged-in manager (e.g. Shubham Shantaram Patil) only sees their own team's report
      const loggedInId = String(userData?.id || storedUser?.id || "").toLowerCase();
      const loggedInEmpId = String(userData?.EmpId || storedUser?.EmpId || "").toLowerCase();
      const loggedInFullName = `${userData?.firstName || userData?.FirstName || storedUser?.firstName || ""} ${userData?.lastName || userData?.LastName || storedUser?.lastName || ""}`.trim().toLowerCase();

      const meetingMgrId = String(m.manager_id || "").toLowerCase();
      const meetingMgrName = String(m.manager_name || "").toLowerCase();

      const isMyTeam = (
        (loggedInId && meetingMgrId === loggedInId) ||
        (loggedInEmpId && meetingMgrId === loggedInEmpId) ||
        (loggedInFullName && (meetingMgrName.includes(loggedInFullName) || loggedInFullName.includes(meetingMgrName)))
      );

      const userRoleLower = (role || storedUser?.role || "").toLowerCase();
      const isSuperAdminAll = userRoleLower === "superadmin_all" || userRoleLower === "admin_all";

      if (!isSuperAdminAll && !isMyTeam) {
        return false;
      }

      if (searchTerm.trim() !== "") {
        const query = searchTerm.toLowerCase();
        const empName = (m.employee_name || "").toLowerCase();
        const empIdStr = (m.employee_id || "").toString().toLowerCase();
        const mgrName = (m.manager_name || "").toLowerCase();

        // Lookup email from map
        const lookup = employeesMap[m.employee_id] || employeesMap[empName] || {};
        const email = (m.employee_email || lookup.email || "").toLowerCase();

        return (
          empName.includes(query) ||
          empIdStr.includes(query) ||
          mgrName.includes(query) ||
          email.includes(query)
        );
      }

      return true;
    });
  }, [rawMeetings, fromDate, toDate, searchTerm, employeesMap, userData, storedUser, role]);

  // Process rows with employee email
  const processedRows = useMemo(() => {
    return filteredMissedMeetings.map((m, idx) => {
      const empName = m.employee_name || "Employee";
      const empNameLower = empName.toLowerCase();
      const lookup = employeesMap[m.employee_id] || employeesMap[empNameLower] || {};

      const email = m.employee_email || lookup.email || "No Email On File";
      const dept = m.Department || lookup.dept || "General";
      const dateVal = m.meet_date || m.date || "-";

      return {
        sno: idx + 1,
        id: m.id || idx,
        employeeName: empName,
        employeeEmail: email,
        department: dept,
        managerName: m.manager_name || "Manager",
        missedDate: dateVal,
        status: "MISSED"
      };
    });
  }, [filteredMissedMeetings, employeesMap]);

  // Unique absent emails for "To" header
  const absentEmails = useMemo(() => {
    const emails = processedRows
      .map((r) => r.employeeEmail)
      .filter((e) => e && e !== "No Email On File" && e.includes("@"));
    return [...new Set(emails)];
  }, [processedRows]);

  // Retrieve logged in user/manager email

  const loggedInUserEmail =
    userData?.email ||
    userData?.EmailId ||
    userData?.Email ||
    userData?.emailId ||
    storedUser?.email ||
    storedUser?.EmailId ||
    storedUser?.Email ||
    storedUser?.emailId ||
    "hr@genesistech.co.in";

  const [serverCcList, setServerCcList] = useState(["hr@genesistech.co.in", loggedInUserEmail]);

  // Download Excel Report (.xlsx)
  const handleDownloadExcel = () => {
    if (processedRows.length === 0) {
      alert("No data available to export to Excel for the selected date range.");
      return;
    }

    const excelData = processedRows.map((r) => ({
      "Sr. No.": r.sno,
      "Employee Name": r.employeeName,
      "Employee Email": r.employeeEmail,
      "Department": r.department,
      "Manager Name": r.managerName,
      "Missed Session Date": r.missedDate,
      "Status": r.status
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Set Column Widths for readability
    worksheet["!cols"] = [
      { wch: 8 },  // Sr. No.
      { wch: 26 }, // Employee Name
      { wch: 32 }, // Employee Email
      { wch: 18 }, // Department
      { wch: 22 }, // Manager Name
      { wch: 18 }, // Missed Date
      { wch: 12 }  // Status
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Missed Sessions");

    XLSX.writeFile(workbook, `1-on-1_Missed_Sessions_Report_${fromDate}_to_${toDate}.xlsx`);
  };

  // Download PDF Report (.pdf)
  const handleDownloadPDF = () => {
    if (processedRows.length === 0) {
      alert("No data available to generate PDF for the selected date range.");
      return;
    }

    try {
      const doc = new jsPDF("p", "pt", "a4");

      // Header Banner Background
      doc.setFillColor(15, 23, 42); // Dark slate
      doc.rect(0, 0, 595, 70, "F");

      // Title & Subtitle
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("GENESIS TECH ENGINEERS", 40, 32);

      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(56, 189, 248); // Cyan accent
      doc.text("1-on-1 Missed Sessions Report", 40, 52);

      // Metadata Info Box
      doc.setFontSize(9);
      doc.setTextColor(71, 85, 105);
      doc.text(`Date Range: ${fromDate} to ${toDate}`, 40, 92);
      doc.text(`Total Records: ${processedRows.length}`, 220, 92);
      doc.text(`Generated On: ${new Date().toLocaleDateString()}`, 400, 92);

      // Table Setup
      const tableColumns = [
        "#", "Employee Name", "Employee Email", "Department", "Manager Name", "Missed Date", "Status"
      ];

      const tableRows = processedRows.map((r) => [
        r.sno,
        r.employeeName,
        r.employeeEmail,
        r.department,
        r.managerName,
        r.missedDate,
        r.status
      ]);

      autoTable(doc, {
        startY: 105,
        head: [tableColumns],
        body: tableRows,
        theme: "grid",
        headStyles: {
          fillColor: [2, 132, 199], // #0284c7 Sky Blue
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: "bold",
          halign: "left"
        },
        bodyStyles: {
          fontSize: 8.5,
          textColor: [30, 41, 59]
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252]
        },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 95 },
          2: { cellWidth: 125 },
          3: { cellWidth: 70 },
          4: { cellWidth: 85 },
          5: { cellWidth: 65 },
          6: { cellWidth: 50, fontStyle: "bold", textColor: [225, 29, 72] }
        },
        margin: { left: 40, right: 40 }
      });

      doc.save(`1-on-1_Missed_Sessions_Report_${fromDate}_to_${toDate}.pdf`);
    } catch (err) {
      console.error("Error generating PDF:", err);
      alert("An error occurred while creating the PDF report.");
    }
  };

  // State for Server-Side Email Template
  const [emailSubject, setEmailSubject] = useState(`Notice: Missed 1-on-1 Meeting Sessions Report (${fromDate} to ${toDate})`);
  const [emailBody, setEmailBody] = useState("");
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  // Fallback body builder
  const generateEmailBodyFallback = () => {
    let body = `Dear Team,\n\nPlease find the list of missed 1-on-1 meeting sessions recorded from ${fromDate} to ${toDate}:\n\n`;

    processedRows.forEach((r, idx) => {
      body += `${idx + 1}. ${r.employeeName} (${r.employeeEmail})\n`;
      body += `   - Missed Session Date: ${r.missedDate}\n`;
      body += `   - Department: ${r.department}\n\n`;
    });

    body += `All team members listed above are requested to connect with their respective managers to reschedule their 1-on-1 session at the earliest.\n\n`;
    body += `Best Regards,\n${userData?.firstName || "Management"} ${userData?.lastName || ""}\nGenesis Tech`;
    return body;
  };

  // Fetch Template & CC List from PHP Server (get_missed_oneonone_email_template.php)
  const fetchEmailTemplateFromPHP = async () => {
    setLoadingTemplate(true);
    try {
      const response = await fetch("/gt-api/get_missed_oneonone_email_template.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          from_date: fromDate,
          to_date: toDate,
          sender_name: `${userData?.firstName || "Management"} ${userData?.lastName || ""}`.trim(),
          sender_email: loggedInUserEmail,
          meetings: processedRows
        })
      });

      const data = await response.json();
      if (data.success) {
        if (data.subject) setEmailSubject(data.subject);
        if (data.body) setEmailBody(data.body);
        if (Array.isArray(data.cc) && data.cc.length > 0) setServerCcList(data.cc);
      } else {
        setEmailBody(generateEmailBodyFallback());
      }
    } catch (err) {
      console.error("Error fetching email template from PHP:", err);
      setEmailBody(generateEmailBodyFallback());
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleOpenEmailModal = () => {
    setEmailSubject(`Notice: Missed 1-on-1 Meeting Sessions Report (${fromDate} to ${toDate})`);
    setShowEmailModal(true);
    fetchEmailTemplateFromPHP();
  };

  const handleOpenMailClient = () => {
    if (absentEmails.length === 0 && processedRows.length === 0) {
      alert("No missed meeting records found in the selected date range.");
      return;
    }

    const toStr = absentEmails.join(",");
    const ccStr = serverCcList.join(",");

    const mailtoUrl = `mailto:${toStr}?cc=${encodeURIComponent(ccStr)}&subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody || generateEmailBodyFallback())}`;
    window.open(mailtoUrl, "_blank");
  };

  const [sendingEmail, setSendingEmail] = useState(false);

  const handleSendServerEmail = async () => {
    if (absentEmails.length === 0 && processedRows.length === 0) {
      alert("No missed meeting records found in the selected date range.");
      return;
    }

    setSendingEmail(true);

    const payload = {
      to: absentEmails,
      cc: serverCcList,
      subject: emailSubject,
      body: emailBody || generateEmailBodyFallback(),
      sender_name: `${userData?.firstName || "Management"} ${userData?.lastName || ""}`.trim(),
      sender_email: loggedInUserEmail,
      from_date: fromDate,
      to_date: toDate
    };

    try {
      const response = await fetch("/gt-api/send_missed_oneonone_email.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      const resText = await response.text();
      let result = null;
      try {
        result = JSON.parse(resText);
      } catch (e) {
        console.log("Raw response from server:", resText);
      }

      if (result && result.success) {
        alert("✅ SUCCESS: Email notification sent via GoDaddy SMTP!\n\nRecipients:\nTo: " + (result.recipients?.to || absentEmails.join(", ")) + "\nCc: " + (result.recipients?.cc || serverCcList.join(", ")));
        setShowEmailModal(false);
      } else if (result && result.message) {
        alert("ℹ️ SERVER RESPONSE: " + result.message);
        setShowEmailModal(false);
      } else {
        alert("✅ SUCCESS: Email request sent to PHP backend server!");
        setShowEmailModal(false);
      }
    } catch (err) {
      console.error("Server email fetch error:", err);
      alert("✅ Email request sent to PHP backend server!");
      setShowEmailModal(false);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCopyEmailDetails = () => {
    const fullText = `TO: ${absentEmails.join(", ")}\nCC: ${serverCcList.join(", ")}\nSUBJECT: ${emailSubject}\n\nBODY:\n${emailBody || generateEmailBodyFallback()}`;
    navigator.clipboard.writeText(fullText).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    });
  };

  return (
    <div className="oneonone-report-container animate-fade">
      {/* HEADER BAR */}
      <div className="report-top-header">
        <div className="header-title-area">
          <button
            className="back-dash-btn"
            onClick={() => handleModuleChange ? handleModuleChange("grid") : window.history.back()}
          >
            ← Back to Dashboard
          </button>

          <div className="header-title-row">
            <span className="header-icon-pill">📊</span>
            <div>
              <h2>1-on-1 Missed Sessions Report</h2>
              <p className="sub-heading">
                Filter date ranges, inspect employee attendance records, export reports, and send email notifications.
              </p>
            </div>
          </div>
        </div>

        <div className="header-actions-area">
          <button className="excel-download-btn" onClick={handleDownloadExcel}>
            📊 Export Excel (.xlsx)
          </button>
          <button className="pdf-download-btn" onClick={handleDownloadPDF}>
            📄 Export PDF (.pdf)
          </button>
          {/* <button className="send-email-btn" onClick={handleOpenEmailModal}>
            ✉️ Send Email to Absent Team
          </button> */}
        </div>
      </div>

      {/* CONTROLS CARD */}
      <div className="report-controls-card">
        <div className="date-filter-group">
          <div className="field-box">
            <label>FROM DATE</label>
            <div className="date-input-wrapper">
              <input
                id="from-date-picker"
                type="date"
                value={fromDate}
                onChange={(e) => handleFromDateChange(e.target.value)}
                className="date-input"
              />
              <span
                className="calendar-input-icon"
                onClick={() => {
                  const el = document.getElementById("from-date-picker");
                  if (el && el.showPicker) el.showPicker();
                  else if (el) el.focus();
                }}
                title="Open Calendar"
              >
                📅
              </span>
            </div>
          </div>

          <div className="field-box">
            <label>TO DATE</label>
            <div className="date-input-wrapper">
              <input
                id="to-date-picker"
                type="date"
                value={toDate}
                onChange={(e) => handleToDateChange(e.target.value)}
                className="date-input"
              />
              <span
                className="calendar-input-icon"
                onClick={() => {
                  const el = document.getElementById("to-date-picker");
                  if (el && el.showPicker) el.showPicker();
                  else if (el) el.focus();
                }}
                title="Open Calendar"
              >
                📅
              </span>
            </div>
          </div>

          {/* <button className="apply-filter-btn" onClick={fetchReportData}>
            Apply Filter 🔍
          </button> */}
        </div>

        <div className="presets-group">
          <span className="preset-label">Quick Presets:</span>
          <button className="preset-chip" onClick={() => handlePreset("thisMonth")}>
            This Month
          </button>
          <button className="preset-chip" onClick={() => handlePreset("lastMonth")}>
            Last Month
          </button>
          <button className="preset-chip" onClick={() => handlePreset("last30")}>
            Last 30 Days
          </button>
        </div>

        <div className="search-box-area">
          <input
            type="text"
            placeholder="Search employee name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* METRICS CARDS */}
      <div className="report-metrics-row">
        <div className="metric-card red-border">
          <span className="metric-icon">❌</span>
          <div className="metric-content">
            <h3>{processedRows.length}</h3>
            <p>Total Missed Sessions</p>
          </div>
        </div>

        <div className="metric-card orange-border">
          <span className="metric-icon">👥</span>
          <div className="metric-content">
            <h3>{new Set(processedRows.map((r) => r.employeeName)).size}</h3>
            <p>Unique Employees Affected</p>
          </div>
        </div>

        <div className="metric-card cyan-border">
          <span className="metric-icon">📅</span>
          <div className="metric-content">
            <h3 style={{ fontSize: "14px", marginTop: "4px" }}>
              {fromDate && toDate ? `${fromDate} to ${toDate}` : "Select Dates Above"}
            </h3>
            <p>Selected Report Period</p>
          </div>
        </div>
      </div>

      {/* DATA TABLE CONTAINER */}
      <div className="report-table-card">
        <div className="table-card-header">
          <h3>Missed 1-on-1 Sessions List ({processedRows.length})</h3>
        </div>

        {loading ? (
          <div className="table-loading-state">
            <div className="loading-spinner"></div>
            <p>Fetching 1-on-1 meeting records from database...</p>
          </div>
        ) : (!fromDate || !toDate) ? (
          <div className="table-empty-state">
            <span className="empty-icon">📅</span>
            <h4>Please Select Date Range</h4>
            <p>Select both <strong>From Date</strong> and <strong>To Date</strong> in the filters above to display missed 1-on-1 meeting records.</p>
          </div>
        ) : processedRows.length === 0 ? (
          <div className="table-empty-state">
            <span className="empty-icon">🎉</span>
            <h4>No Missed Meetings Found</h4>
            <p>No 1-on-1 meetings were missed in the selected date range ({fromDate} to {toDate}).</p>
          </div>
        ) : (
          <div className="report-table-wrapper">
            <table className="report-custom-table">
              <thead>
                <tr>
                  <th className="one-sno-col">#</th>
                  <th className="one-emp-col">EMPLOYEE NAME</th>
                  <th className="one-email-col">EMAIL ADDRESS</th>
                  <th className="one-dept-col">DEPARTMENT</th>
                  <th className="one-mgr-col">MANAGER</th>
                  <th className="one-date-col">MISSED DATE</th>
                  <th className="one-status-col">STATUS</th>
                </tr>
              </thead>
              <tbody>
                {processedRows.map((row) => (
                  <tr key={row.id + "-" + row.sno}>
                    <td className="one-sno-col">{row.sno}</td>
                    <td className="one-emp-col">
                      <strong>{row.employeeName}</strong>
                    </td>
                    <td className="one-email-col">{row.employeeEmail}</td>
                    <td className="one-dept-col">{row.department}</td>
                    <td className="one-mgr-col">{row.managerName}</td>
                    <td className="one-date-col">
                      <span className="date-badge">{row.missedDate}</span>
                    </td>
                    <td className="one-status-col">
                      <span className="status-badge-missed">❌ MISSED</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* EMAIL PREVIEW / ACTION MODAL */}
      {showEmailModal && ReactDOM.createPortal(
        <div className="email-modal-overlay" onClick={() => setShowEmailModal(false)}>
          <div className="email-modal-card" onClick={(e) => e.stopPropagation()}>

            <div className="modal-header">
              <div className="modal-header-left">
                <span className="modal-icon-badge">✉️</span>
                <div>
                  <h3>Send Email Notification</h3>
                  <p className="modal-sub-text">Dispatch notifications to team members with missed 1-on-1 sessions</p>
                </div>
              </div>
              <button className="modal-close-btn" onClick={() => setShowEmailModal(false)}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="recipient-field">
                <label className="field-label">
                  <span className="label-text">To (Absent Employees):</span>
                </label>
                <div className="chips-container">
                  {absentEmails.length > 0 ? (
                    absentEmails.map((email, idx) => (
                      <span key={idx} className="email-chip">
                        📧 {email}
                      </span>
                    ))
                  ) : (
                    <span className="no-emails-text">No employee email addresses found</span>
                  )}
                </div>
              </div>

              <div className="recipient-field">
                <label className="field-label">
                  <span className="label-text">CC (Fixed Recipients):</span>
                </label>
                <div className="chips-container">
                  {serverCcList.map((email, idx) => (
                    <span key={idx} className="email-chip cc-chip">
                      👤 {email} {email.toLowerCase().includes("hr@") ? "(HR)" : "(LoggedIn Manager)"}
                    </span>
                  ))}
                </div>
              </div>

              <div className="preview-field">
                <label className="field-label">
                  <span className="label-text">Subject</span>
                </label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="preview-subject-input"
                  placeholder="Enter email subject..."
                />
              </div>

              <div className="preview-field">
                <label className="field-label">
                  <span className="label-text">Email Message Body</span>
                </label>
                {loadingTemplate ? (
                  <div className="loading-template-box">
                    <div className="mini-spinner"></div>
                    <span>Loading template from PHP server...</span>
                  </div>
                ) : (
                  <textarea
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    rows={8}
                    className="preview-textarea"
                  />
                )}
              </div>
            </div>

            <div className="modal-footer">
              {/* <button className="copy-details-btn" onClick={handleCopyEmailDetails}>
                {copySuccess ? "✔ Copied!" : "📋 Copy Text"}
              </button> */}

              {/* <button className="launch-mail-btn" onClick={handleOpenMailClient}>
                💻 Mail App
              </button> */}

              <button className="server-mail-btn" onClick={handleSendServerEmail} disabled={sendingEmail}>
                {sendingEmail ? "⏳ Dispatching..." : "⚡ Send Email"}
              </button>
            </div>

          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default OneOnOneReport;
